import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { useAppSelector } from 'hooks/redux';
import { OrderedMap } from 'immutable';
import { DateTime } from 'luxon';
import React, { useEffect, useState, useReducer, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';

import type { Account, accountsTypes } from 'app-components/flux/accounts';
import { accountsSelectors } from 'app-components/flux/accounts';
import { accountsBalancesActions } from 'app-components/flux/accounts-balances';
import { getCommonConfig, tracker } from 'app-components/utils/core';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Fade from '@mui/material/Fade';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import QPanelButton from 'components/QPanelButton';
import AccountField from 'components/QuickenControls/AccountField';

import { actionHelpers } from 'app-components/flux/core';
import StdDialog from 'components/Dialogs/StdDialog';
import QTip from 'components/QuickenControls/QTip';

import { documentsActions, documentsTypes } from 'app-components/flux/documents';
import importAsync from 'components/ImportFromMint/importAsync';
import LoadingView from 'components/LoadingView';
import QButton, { QButtonVariantEnum } from 'components/QButton';
import ReportProblemLink from 'components/ReportProblemLink';
import { appRefreshAll } from 'data/app/actions';
import { csvImportFilesFailed, csvImportFilesInProgress, csvImportFilesSucceed } from 'data/app/selectors';
import { importData, pfmImportHistoricalBalancesImport, pfmImportParsePost } from 'data/import/importActions';
import { registerCallback, unregisterCallback } from 'data/import/importSagas';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import checkLargeMotion from 'assets/illustrations/Check-large-motion.png';

import '!file-loader?name=[folder]/[name].[ext]!../../../files/balance template.csv';
import '!file-loader?name=[folder]/[name].[ext]!../../../files/import template.csv';
import DeleteForever from '@mui/icons-material/DeleteForever';
import Help from '@mui/icons-material/Help';
import Info from '@mui/icons-material/Info';
import IconButton from '@mui/material/IconButton';

import MintExtension from 'assets/images/addFI/MintExtension.png';
import AppleIcon from 'assets/logos/apple.svg';
import FileIcon from 'assets/logos/csvFile.svg';
import MintIcon from 'assets/logos/mint.svg';
import PersonalCapitalIcon from 'assets/logos/personalCapital.svg';
import ImportFromMint from 'components/ImportFromMint';

import type { HistoricalBalancePayloadType, SuccessResponseType } from 'data/import/importTypes';

import MintMatchDialog from './MintMatchDialog';
import type Screens from './screens';

const ViewStateEnum = Object.freeze({
  IMPORT_FROM: 'IMPORT_FROM',
  MINT_INSTRUCTIONS: 'MINT_INSTRUCTIONS',
  MINT_MATCH: 'MINT_MATCH',
  MINT_BALANCE_ACCOUNT: 'MINT_BALANCE_ACCOUNT',
  MINT_BALANCE: 'MINT_BALANCE',
  MINT_SUCCESS: 'MINT_SUCCESS',
  MINT_EXTENSION_SUCCESS: 'MINT_EXTENSION_SUCCESS',
  MINT_EXTENSION_CLEAR_ALL: 'MINT_EXTENSION_CLEAR_ALL',
  BALANCE_SUCCESS: 'BALANCE_SUCCESS',
  MINT_IMPORT_PROGRESS: 'MINT_IMPORT_PROGRESS',
  PERSONAL_CAPITAL_INSTRUCTIONS: 'PERSONAL_CAPITAL_INSTRUCTIONS',
  APPLE_CARD_INSTRUCTIONS: 'APPLE_CARD_INSTRUCTIONS',
  OTHER_INSTRUCTIONS: 'OTHER_INSTRUCTIONS',
  FILE_PROCESSING: 'FILE_PROCESSING',
  FILE_SUCCEED: 'FILE_SUCCEED',
  FILE_PARTIALLY_SUCCEED: 'FILE_PARTIALLY_SUCCEED',
  FILE_FAILED: 'FILE_FAILED',
});

const contentStyle = {
  width: '100%',
  height: '100%',
};

const useStyles = makeStyles()((theme) => ({
  importFromPageStyles: {
    // width: 750,
    display: 'flex',
    flexDirection: 'row',
  },
  tabs: {
    display: 'flex',
    flexShrink: 0,
    paddingBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    '& .MuiTabs-scroller': {
      borderBottom: `1px solid ${theme.palette.border.main}`,
    },
  },
  typeTabs: {
    display: 'flex',
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
    '& .MuiTabs-scroller': {
      borderBottom: `1px solid ${theme.palette.border.main}`,
    },
  },
  importFromPageRight: {
    margin: 'auto',
    height: '100%',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 645,
    alignItems: 'center',
    overflowX: 'hidden',
    overflowY: 'auto',
    '&.mintNoPadding': {
      padding: 0,
    },
  },
  contentWrapper: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  contentPlaceholder: {
    ...contentStyle,
    position: 'static',
    visibility: 'hidden',
  },
  content: {
    ...contentStyle,
    position: 'absolute',
    top: 0,
    transition: 'all 300ms',
  },
  selectContent: {
    ...contentStyle,
    position: 'absolute',
    top: 0,
    transition: 'all 300ms',
  },
  instructions: {
    maxWidth: 860,
    width: 810,
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    margin: 40,
  },
  statusImage: {
    pointerEvents: 'none',
    width: 128,
    height: 128,
    marginTop: 24,
    marginBottom: 24,
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  listItem: {
    display: 'flex',
    alignItems: 'baseline',
    marginTop: 20,
    marginBottom: 20,
    textAlign: 'left',
  },
  listItemMint: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: 12,
    textAlign: 'left',
    flexDirection: 'column',
  },
  step: {
    marginRight: '20px',
    minWidth: 60,
  },
  table: {
    marginTop: 8,
    marginBottom: 8,
  },
  header: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.border.light}`,
    padding: 6,
  },
  cell: {
    padding: 6,
  },
  dropZoneContent: {
    height: 85,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    alignContent: 'center',
    border: `2px dashed ${theme.palette.border.light}`,
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    marginTop: 15,
  },
  fromHeader: {
    width: 610,
    color: theme.palette.text.secondary,
    marginBottom: '30px',
    marginTop: '20px',
  },
  selectAcctRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  selectAcctLeft: {
    display: 'flex',
    flexDirection: 'column',
  },
  miniHeader: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
  },
  selectAcctRightTxt: {
    marginTop: '40px',
    marginLeft: '20px',
    width: '240px',
    letterSpacing: '0.25px',
  },
  paragraph: {
    width: '550px',
    textAlign: 'left',
  },
  bottomButton: {
    width: '96px',
    height: '36px',
  },
  dragAndDropInnerItems: {
    textAlign: 'center',
    margin: 'auto',
    alignItems: 'center',
  },
  dragAndDropTopTxt: {
    pointerEvents: 'none',
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  dragAndDropMiddleTxt: {
    pointerEvents: 'none',
    marginRight: 8,
    marginLeft: 8,
    fontSize: 14,
  },
  dragAndDropBrowseBtn: {
    width: '96px',
    height: '36px',
    backgroundColor: theme.palette.background.paper,
  },
  boldText: {
    fontWeight: 500,
  },
  acceptedFile: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: '4px',
    padding: '8px',
  },
  deleteIcon: {
    color: theme.palette.error.main,
    padding: '1px',
    marginTop: -2,
    marginLeft: 5,
  },
  fileDropText: {
    display: 'flex',
    alignItems: 'center',
  },
  mintInstructions: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  mintBalanceInstructions: {
    display: 'flex',
    paddingBottom: 8,
    paddingTop: 16,
  },
  balanceImageWidth: {
    width: 345,
    marginBottom: 8,
    borderRadius: 8,
  },
  mintInstructionsText: {
    flexDirection: 'column',
    marginLeft: 16,
  },
  noteSection: {
    display: 'flex',
    color: theme.palette.text.secondary,
    alignItems: 'center',
  },
  textAlignCenter: {
    textAlign: 'center',
  },
  marginForty: {
    margin: 40,
  },
  marginRightAuto: {
    marginRight: 'auto',
  },
  leaveOpenSection: {
    color: theme.palette.info.dark,
    backgroundColor: theme.palette.info.light,
    borderRadius: 8,
    padding: 16,
  },
  errorText: {
    color: theme.palette.error.main,
  },
  errorIcon: {
    marginLeft: 2,
    marginRight: 2,
    height: 18,
    color: theme.palette.error.main,
  },
  infoIcon: {
    height: 18,
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    padding: 8,
    borderRadius: 8,
    width: 485,
    backgroundColor: theme.palette.error.light,
  },
  panel: {
    background: theme.palette.background.default,
    margin: '0px 0px 24px 0px',
  },
  roundedImage: {
    borderRadius: 30,
  },
  csvImage: {
    width: 48,
  },
  mintBalanceAccountSelect: {
    height: 440,
    maxWidth: 660,
    width: 610,
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  mintBalanceAccountPadding: {
    paddingTop: 12,
    paddingBottom: 24,
    textAlign: 'center',
  },
  successRoot: {
    height: 360,
    paddingRight: 42,
    paddingLeft: 42,
  },
  successStatusContainer: {
    paddingTop: 36,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  thumbsUpWidth: {
    width: 124,
  },
  finishLater: {
    textAlign: 'center',
    margin: '34px 132px 24px 132px',
    display: 'flex',
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  openLink: {
    height: 16,
    width: 16,
    marginLeft: 2,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  clearWarningBanner: {
    display: 'flex',
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
    gap: 8,
    borderRadius: 8,
    marginBottom: 36,
    marginTop: 36,
    width: 600,
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  clearActionSelection: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.info.light,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderRadius: 6,
    marginRight: 'auto',
    marginLeft: 'auto',
    marginBottom: 40,
  },
  clearingText: {
    width: 450,
    marginBottom: 36,
    textAlign: 'center',
  },
  errorStyle: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
  },
}));

const importMessage = (
  <Typography variant='paragraphRegular' component='div'>
    Upload the CSV file you just created below.
  </Typography>
);

const FileImportDialog = (props: {
  account?: accountsTypes.Account | null;
  onHandleClose: () => void;
  onFinishLater?: () => void;
  open: boolean;
  isGSFImport?: boolean;
  setIsLinking?: React.Dispatch<React.SetStateAction<boolean>>;
  startWithBalancesTab?: boolean;
  hideTabBar?: boolean;
}) => {
  const {
    account,
    onHandleClose,
    onFinishLater,
    open,
    isGSFImport,
    setIsLinking,
    startWithBalancesTab,
    hideTabBar,
    ...otherProps
  } = props;
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const showMintButtons = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'mintImportExtension'));
  const [importTypeTab, setImportTypeTab] = useState(startWithBalancesTab ? 1 : 0);
  const [myAcceptedFiles, setMyAcceptedFiles] = useState<File[]>([]);
  const [mintTabValue, setMintTabValue] = useState(showMintButtons ? 'IMPORT_EXTENSION' : 'IMPORT_ACCOUNTS');
  const [mintAccounts, setMintAccounts] = useState<
    null | { accountName: string; enabled: boolean; id: string; txnCount: number }[]
  >(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<SuccessResponseType | null>(null);
  const [loadingViewTitle, setLoadingViewTitle] = useState(
    `Importing your ${importTypeTab ? 'balances' : 'transactions'}`,
  );
  const [files, setFiles] = useState<OrderedMap<string, File>>(OrderedMap());

  const filesInProgress = useSelector((state) => csvImportFilesInProgress(state, files));
  const filesComplete = useSelector((state) => csvImportFilesSucceed(state, files));
  const filesFailed = useSelector((state) => csvImportFilesFailed(state, files));
  const [errors, setErrors] = useState<string[] | JSX.Element[]>([]);

  const handleDeleteFile = useCallback(() => {
    setFiles(OrderedMap());
    setMyAcceptedFiles([]);
  }, []);

  const [viewState, dispatchViewStateAction] = useReducer(
    // @ts-expect-error FIXME TS7006
    (currentViewState, action: Screens) => {
      let newViewState = currentViewState;
      switch (action) {
        case 'importFrom':
          newViewState = {
            previousView: currentViewState.currentView,
            previousLeft: '+200%',
            currentView: ViewStateEnum.IMPORT_FROM,
          };
          break;
        case 'mintInstructions':
          newViewState = {
            previousView: currentViewState.currentView,
            previousLeft: '-100%',
            currentView: ViewStateEnum.MINT_INSTRUCTIONS,
          };
          break;
        case 'mintBalance':
          newViewState = {
            previousView: currentViewState.currentView,
            previousLeft: '-100%',
            currentView: ViewStateEnum.MINT_BALANCE,
          };
          break;
        case 'mintBalanceAccount':
          newViewState = {
            previousView: currentViewState.currentView,
            previousLeft: '-100%',
            currentView: ViewStateEnum.MINT_BALANCE_ACCOUNT,
          };
          break;
        case 'mintMatch':
          newViewState = {
            previousView: ViewStateEnum.MINT_INSTRUCTIONS,
            previousLeft: '-100%',
            currentView: ViewStateEnum.MINT_MATCH,
          };
          break;
        case 'mintSuccess':
          setMyAcceptedFiles([]);
          newViewState = {
            previousView: currentViewState.currentView,
            previousLeft: '-100%',
            currentView: ViewStateEnum.MINT_SUCCESS,
          };
          break;
        case 'balanceSuccess':
          newViewState = {
            previousView: currentViewState.currentView,
            previousLeft: '-100%',
            currentView: ViewStateEnum.BALANCE_SUCCESS,
          };
          break;
        case 'mintLoading':
          newViewState = {
            previousView: undefined,
            previousLeft: '-100%',
            currentView: ViewStateEnum.FILE_PROCESSING,
          };
          break;
        case 'mintImportProgress':
          newViewState = {
            previousView: undefined,
            previousLeft: '-100%',
            currentView: ViewStateEnum.MINT_IMPORT_PROGRESS,
          };
          break;
        case 'mintExtensionSuccess':
          newViewState = {
            previousView: undefined,
            previousLeft: '-100%',
            currentView: ViewStateEnum.MINT_EXTENSION_SUCCESS,
          };
          break;
        case 'mintExtensionClearAll':
          newViewState = {
            previousView: undefined,
            previousLeft: '-100%',
            currentView: ViewStateEnum.MINT_EXTENSION_CLEAR_ALL,
          };
          break;
        case 'personalCapitalInstructions':
          newViewState = {
            previousView: currentViewState.currentView,
            previousLeft: '-100%',
            currentView: ViewStateEnum.PERSONAL_CAPITAL_INSTRUCTIONS,
          };
          break;
        case 'appleCardInstructions':
          newViewState = {
            previousView: currentViewState.currentView,
            previousLeft: '-100%',
            currentView: ViewStateEnum.APPLE_CARD_INSTRUCTIONS,
          };
          break;
        case 'otherInstructions':
          newViewState = {
            previousView: currentViewState.currentView,
            previousLeft: '-100%',
            currentView: ViewStateEnum.OTHER_INSTRUCTIONS,
          };
          break;
        case 'updateFileState':
          if (filesInProgress.size) {
            newViewState = {
              previousView: undefined,
              currentView: ViewStateEnum.FILE_PROCESSING,
            };
          } else if (filesFailed.size) {
            newViewState = {
              previousView: currentViewState.currentView,
              currentView: ViewStateEnum.FILE_FAILED,
            };
          } else if (filesComplete.size) {
            if (errors?.length) {
              newViewState = {
                previousView: currentViewState.currentView,
                currentView: ViewStateEnum.FILE_PARTIALLY_SUCCEED,
              };
            } else {
              newViewState = {
                previousView: currentViewState.currentView,
                currentView: ViewStateEnum.FILE_SUCCEED,
              };
            }
          }
          break;
        case 'tryAgain':
          newViewState = {
            previousView: currentViewState.currentView,
            currentView: ViewStateEnum.IMPORT_FROM,
          };
          break;
        default:
          assert(false, `'${action}' - unknown action`);
          newViewState = {
            previousView: currentViewState.currentView,
            currentView: ViewStateEnum.IMPORT_FROM,
          };
      }
      const view = document.getElementById('import-dialog');
      if (view) {
        view.scrollTo(0, 0);
      }
      return newViewState;
    },
    {
      previousView: undefined,
      previousLeft: undefined,
      currentView: ViewStateEnum.IMPORT_FROM,
    },
  );

  const dropzone = useDropzone({
    accept: [
      viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS && mintTabValue === 'IMPORT_EXTENSION'
        ? '.json'
        : '.csv',
    ],
  });
  const { acceptedFiles, getRootProps, open: dropzoneOpen, getInputProps, isDragActive, isFileDialogActive } = dropzone;
  const { onClick, ...dropzoneRootProps } = getRootProps();

  const showHistoricalBalances = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'importMintBalances'),
  );
  const acceptableAccounts = useSelector(accountsSelectors.getAccountsById);
  const [importAccount, setImportAccount] = useState(account?.id ? acceptableAccounts.get(account.id) : undefined);

  useEffect(() => {
    if (acceptedFiles.length - 1 >= 0) {
      setMyAcceptedFiles([acceptedFiles[acceptedFiles.length - 1]]);
    }
  }, [acceptedFiles]);

  const mintImportAsync = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'mintImportAsync'));

  useEffect(() => {
    const newErrors: JSX.Element[] = [];
    // @ts-expect-error FIXME TS7006
    filesComplete.forEach((file) =>
      // @ts-expect-error FIXME TS7006
      file.data?.errors?.forEach?.((error) => {
        newErrors.push(
          <Typography display='block' paragraph>
            <Typography variant='bodyRegular' display='block'>
              {`${newErrors.length + 1}. ${error.message}`}
            </Typography>
            <Typography variant='paragraphRegular' display='block'>
              Row: {error.recordNumber}&nbsp;&nbsp;&nbsp; Column: {error.columnName}&nbsp;&nbsp;&nbsp; Value:{' '}
              <Box bgcolor='warning.light' display='inline'>
                {error.value}
              </Box>
            </Typography>
            <Typography variant='paragraphRegular' display='block'>
              [
              {error.row?.map?.((value: any, index: number) => (
                <>
                  <Box bgcolor={error.columnIndex === index ? 'warning.light' : undefined} display='inline'>
                    {value}
                  </Box>
                  {index !== error.row.length - 1 && ', '}
                </>
              ))}
              ]
            </Typography>
          </Typography>,
        );
      }),
    );
    setErrors(newErrors);
  }, [filesComplete]);

  const onHandleCloseOverride = useCallback(() => {
    setMyAcceptedFiles([]);
    if (onHandleClose) {
      onHandleClose();
    }
  }, [onHandleClose]);

  const handleChange = useCallback(
    // @ts-expect-error FIXME TS7006
    (_event, value) => {
      handleDeleteFile();
      setMintTabValue(value);
    },
    [handleDeleteFile],
  );

  const handleImportTypeChange = useCallback(
    // @ts-expect-error FIXME TS7006
    (_event, value) => {
      setLoadingViewTitle(`Importing your ${value ? 'balances' : 'transactions'}`);
      setImportTypeTab(value);
    },
    [setImportTypeTab],
  );

  const handleExtensionImport = useCallback(
    // @ts-expect-error FIXME TS7006
    (clearAccountsCategories) => {
      const file = myAcceptedFiles[0];
      file?.text().then((json) => {
        try {
          const fileJS = JSON.parse(json);
          dispatchViewStateAction('mintImportProgress');
          importAsync(fileJS, clearAccountsCategories)
            .then((mintImportState) => {
              setSuccessData({
                unlinkedAccountsCount: 1,
                // @ts-expect-error FIXME TS18046
                totalTxnCount: mintImportState.TRANSACTIONS.importedCount,
                // @ts-expect-error FIXME TS18046
                oldestTxnDate: mintImportState.TRANSACTIONS.earliestDate,
              });
              dispatchViewStateAction('mintExtensionSuccess');
              dispatch(appRefreshAll());
            })
            .catch(() => {
              handleDeleteFile();
              dispatchViewStateAction('mintInstructions');
              setErrors(['Error occured']);
            });
        } catch {
          handleDeleteFile();
          dispatchViewStateAction('mintInstructions');
          setErrors(['Error occured']);
        }
      });
    },
    [dispatch, handleDeleteFile, myAcceptedFiles],
  );

  const errorBox = useMemo(
    () => (
      <div className={classes.error} style={{ width: '100%', justifyContent: 'center', flexDirection: 'column' }}>
        <div className={classes.errorStyle}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography className={classes.errorText} variant='paragraphRegular'>
            An error occurred during CSV file import. Try a different file or try again.
          </Typography>
        </div>
        <ReportProblemLink
          feedbackSubject='importing data'
          attachments={{ 'import_data.json': myAcceptedFiles[0] }}
          sx={{ margin: 1 }}
        >
          <ScienceOutlinedIcon />
          &nbsp; report anything unexpected
        </ReportProblemLink>
      </div>
    ),
    [classes.error, classes.errorIcon, classes.errorStyle, classes.errorText, myAcceptedFiles],
  );

  const [providerSelected, setProviderSelected] = useState<string | null>();
  const handleImport = useCallback(() => {
    if (
      myAcceptedFiles?.length &&
      dispatch &&
      (importAccount ||
        viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS ||
        viewState.currentView === ViewStateEnum.MINT_BALANCE ||
        viewState.currentView === ViewStateEnum.MINT_BALANCE_ACCOUNT)
    ) {
      let provider: any;
      switch (viewState.currentView) {
        case ViewStateEnum.MINT_BALANCE:
          provider = 'MINT-BALANCES';
          setProviderSelected('Mint-Balances');
          break;
        case ViewStateEnum.MINT_INSTRUCTIONS:
          switch (mintTabValue) {
            case 'IMPORT_ACCOUNTS':
              provider = 'MINT';
              setProviderSelected('Mint');
              dispatchViewStateAction('mintLoading');
              break;
            default:
              assert(false, `unexpected mintTabValue = ${mintTabValue}`);
          }
          break;
        case ViewStateEnum.PERSONAL_CAPITAL_INSTRUCTIONS:
          provider = 'PERSONAL_CAPITAL';
          setProviderSelected('Personal Capital');
          break;
        case ViewStateEnum.APPLE_CARD_INSTRUCTIONS:
          provider = 'APPLE_CARD';
          setProviderSelected('Apple Card');
          break;
        case ViewStateEnum.OTHER_INSTRUCTIONS:
          if (importTypeTab) {
            provider = 'SIMPLIFI-BALANCES';
            setProviderSelected('CSV-BALANCES');
          } else {
            provider = 'SIMPLIFI';
            setProviderSelected('CSV');
          }
          break;
        default:
          provider = undefined;
      }
      if (provider) {
        const clientId = uuidv4();
        if (provider === 'MINT') {
          tracker.track(tracker.events.csvImport, { status: 'start', type: 'Mint' });
          myAcceptedFiles.forEach((file) => {
            setFiles((filesMap) => filesMap.set(clientId, file));
            new Promise((resolve, reject) => {
              dispatch(
                documentsActions.uploadDocument(
                  {
                    clientId,
                    document: documentsTypes.mkDocument({
                      name: file.name,
                      documentType: 'OTHER',
                      contentType: file.type || 'application/octet-stream',
                      clientId,
                      size: file.size,
                    }),
                    data: file,
                  },
                  { resolve, reject },
                ),
              );
            })
              .then((response: { id: string }) => {
                actionHelpers
                  .bindPromiseAction(
                    dispatch,
                    pfmImportParsePost,
                  )(response.id)
                  .then((response2: any) => {
                    setErrors([]);
                    setMintAccounts(response2.data.accounts);
                    dispatchViewStateAction('mintMatch');
                    dispatch(documentsActions.deleteDocument(documentsTypes.mkDocument({ id: response.id })));
                  })
                  .catch((error) => {
                    setErrors(error.response.data.errors);
                    setMyAcceptedFiles([]);
                    dispatchViewStateAction('mintInstructions');
                    dispatch(documentsActions.deleteDocument(documentsTypes.mkDocument({ id: response.id })));
                  });
              })
              .catch((_error) => {
                setErrors(['File upload failed']);
                dispatchViewStateAction('tryAgain');
              });
          });
        } else if (
          (provider === 'SIMPLIFI-BALANCES' || provider === 'MINT-BALANCES') &&
          showHistoricalBalances &&
          importAccount
        ) {
          tracker.track(tracker.events.csvImport, { status: 'balancesStart', type: provider });
          myAcceptedFiles.forEach((file) => {
            setFiles((filesMap) => filesMap.set(clientId, file));
            new Promise((resolve, reject) => {
              dispatch(
                documentsActions.uploadDocument(
                  {
                    clientId,
                    document: documentsTypes.mkDocument({
                      name: file.name,
                      documentType: 'OTHER',
                      contentType: file.type || 'application/octet-stream',
                      clientId,
                      size: file.size,
                    }),
                    data: file,
                  },
                  { resolve, reject },
                ),
              );
            })
              .then((response: { id: string }) => {
                setDocumentId(response.id);
                actionHelpers
                  .bindPromiseAction(
                    dispatch,
                    documentsActions.getDocument,
                  )({ id: response.id })
                  .then((docResp: any) => {
                    if (!importAccount.id) return;
                    const postObj: HistoricalBalancePayloadType = {
                      documentId: response.id,
                      accountId: importAccount.id,
                      provider: provider === 'SIMPLIFI-BALANCES' ? 'SIMPLIFI' : 'MINT',
                      clientId,
                    };
                    actionHelpers
                      .bindPromiseAction(
                        dispatch,
                        pfmImportHistoricalBalancesImport,
                      )(postObj)
                      .then((_response2) => {
                        // for now do nothing since we are using bump
                      })
                      .catch((error) => {
                        setErrors(error.response.data.errors);
                        setMyAcceptedFiles([]);
                        dispatchViewStateAction(
                          providerSelected === 'Mint-Balances' ? 'mintBalance' : 'otherInstructions',
                        );
                        dispatch(documentsActions.deleteDocument(documentsTypes.mkDocument({ id: docResp.data.id })));
                      });
                  })
                  .catch((error) => assert(false, error));
              })
              .catch((_error) => {
                setErrors(['File upload failed']);
                dispatchViewStateAction(providerSelected === 'Mint-Balances' ? 'mintBalance' : 'otherInstructions');
              });
          });
        } else if (importAccount?.id) {
          const importAccountId = importAccount.id; // Have to copy so ts is not confused by forEach lambda
          myAcceptedFiles.forEach((file) => {
            setFiles((filesMap) => filesMap.set(clientId, file));
            dispatch(
              importData({
                clientId,
                accountId: importAccountId,
                provider,
                document: documentsTypes.mkDocument({
                  name: file.name,
                  documentType: 'OTHER',
                  contentType: file.type || 'text/csv',
                  clientId: uuidv4(),
                  size: file.size,
                }),
                data: file,
              }),
            );
          });
        }
      }
    }
  }, [
    myAcceptedFiles,
    dispatch,
    importAccount,
    viewState.currentView,
    mintTabValue,
    importTypeTab,
    showHistoricalBalances,
    providerSelected,
  ]);

  const handleMintImportMore = useCallback(() => {
    handleDeleteFile();
    dispatchViewStateAction('mintInstructions');
  }, [handleDeleteFile]);

  useEffect(() => {
    if (open) {
      setFiles(OrderedMap());
      if (isGSFImport) {
        dispatchViewStateAction('mintInstructions');
      } else {
        dispatchViewStateAction('importFrom');
      }
    }
  }, [isGSFImport, open]);

  useEffect(() => {
    dispatchViewStateAction('updateFileState');
  }, [filesInProgress, filesComplete, filesFailed]);

  useEffect(() => {
    if (mintImportAsync && !importTypeTab) {
      // if we wanna flip back and forth from async ff
      registerCallback((data: { status: string; extData: SuccessResponseType | null }) => {
        if (data.status === 'COMPLETE') {
          if (data.extData) {
            setSuccessData(data.extData);
          }
          setErrors([]);
          dispatchViewStateAction('mintSuccess');
        } else if (data.status === 'TIMEOUT') {
          setLoadingViewTitle('Hang tight... Importing is taking a while');
        } else {
          setErrors(['An error ocurred. Try a different file or try again.']);
          handleDeleteFile();
          dispatchViewStateAction('mintInstructions');
        }
      });

      return () => {
        unregisterCallback();
      };
    }
    if (importTypeTab && showHistoricalBalances) {
      registerCallback((data: { destinationUrl: string; status: string; extData: SuccessResponseType | null }) => {
        if (data.destinationUrl === '/pfm-importer/import-balances') {
          if (data.status === 'COMPLETE') {
            if (data.extData) {
              setSuccessData(data.extData);
              dispatch(documentsActions.deleteDocument(documentsTypes.mkDocument({ id: data.extData.documentId })));
            }
            setErrors([]);
            dispatch(accountsBalancesActions.getAccountsBalances());
            dispatchViewStateAction('balanceSuccess');
          } else if (data.status === 'TIMEOUT') {
            setLoadingViewTitle('Hang tight... Importing is taking a while');
          } else {
            setErrors(['An error ocurred. Try a different file or try again.']);
            handleDeleteFile();
            if (documentId) {
              dispatch(documentsActions.deleteDocument(documentsTypes.mkDocument({ id: documentId })));
            }
            dispatchViewStateAction(providerSelected === 'Mint-Balances' ? 'mintBalance' : 'otherInstructions');
          }
        }
      });
      return () => {
        unregisterCallback();
      };
    }
    return undefined;
  }, [
    dispatch,
    documentId,
    handleDeleteFile,
    importTypeTab,
    mintImportAsync,
    providerSelected,
    showHistoricalBalances,
  ]);

  const dragDropComponent = useMemo(
    () => (
      <div className={classes.dropZoneContent} {...(isFileDialogActive ? {} : dropzoneRootProps)}>
        {isDragActive ? (
          <CloudUploadIcon className={classes.statusImage} />
        ) : (
          <div className={classes.dragAndDropInnerItems}>
            {myAcceptedFiles.length ? (
              <div className={classes.acceptedFile}>
                {myAcceptedFiles[0].name}
                <IconButton
                  className={classes.deleteIcon}
                  aria-label='Delete'
                  id='delete-file'
                  onClick={handleDeleteFile}
                  size='large'
                >
                  <DeleteForever />
                </IconButton>
              </div>
            ) : (
              <div className={classes.fileDropText}>
                <Typography variant='bodyMedium' className={classes.dragAndDropTopTxt}>
                  Drag and drop your&nbsp;
                  {viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS && mintTabValue === 'IMPORT_EXTENSION'
                    ? 'JSON'
                    : 'CSV'}
                  &nbsp;file here
                </Typography>
                <Typography variant='paragraphRegular' className={classes.dragAndDropMiddleTxt}>
                  or
                </Typography>
                <QButton variant='outlined' onClick={() => dropzoneOpen()} className={classes.dragAndDropBrowseBtn}>
                  BROWSE
                </QButton>
              </div>
            )}
          </div>
        )}
      </div>
    ),
    [
      classes.dropZoneContent,
      classes.statusImage,
      classes.dragAndDropInnerItems,
      classes.acceptedFile,
      classes.deleteIcon,
      classes.fileDropText,
      classes.dragAndDropTopTxt,
      classes.dragAndDropMiddleTxt,
      classes.dragAndDropBrowseBtn,
      isFileDialogActive,
      dropzoneRootProps,
      isDragActive,
      myAcceptedFiles,
      handleDeleteFile,
      viewState.currentView,
      mintTabValue,
      dropzoneOpen,
    ],
  );

  const accountDropdown = useMemo(
    () => (
      <AccountField
        accounts={acceptableAccounts}
        initialAccountId={importAccount?.id}
        onSelected={(selectedAccount: Account) => setImportAccount(selectedAccount)}
        label='Account'
        variant='filled'
        margin='normal'
        required
        doNotIncludeClosed
        fullWidth
      />
    ),
    [acceptableAccounts, importAccount?.id],
  );

  const importFromPage = useMemo(
    () => (
      <div className={classes.importFromPageStyles}>
        <div className={classes.importFromPageRight}>
          {!hideTabBar && showHistoricalBalances && (
            <Tabs
              aria-label='import type tabs'
              value={importTypeTab}
              onChange={handleImportTypeChange}
              className={classes.typeTabs}
            >
              <Tab label='Import Transactions' />
              <Tab label='Import Historical Balance' />
            </Tabs>
          )}
          <Typography variant='bodyRegular' className={classes.fromHeader}>
            {importTypeTab
              ? 'You can manually import historical balances from Mint or your own CSV to see historical data that you can’t automatically sync in Simplifi.'
              : 'You can manually import transactions from other financial apps or from accounts that you can’t automatically sync to Simplifi.'}
          </Typography>
          <div>
            <QPanelButton
              id='import-mint'
              title='Import from Mint'
              subtitle='Import a CSV file created by Mint'
              onClick={() => dispatchViewStateAction(importTypeTab ? 'mintBalanceAccount' : 'mintInstructions')}
              tabIndex={0}
              className={classes.panel}
              startIcon={<img src={MintIcon} alt={'Mint Logo'} className={classes.roundedImage} />}
            />
            {!importTypeTab && (
              <>
                <QPanelButton
                  id='import-personal-capital'
                  title='Import from Personal Capital'
                  subtitle='Step-by-step instructions'
                  onClick={() => dispatchViewStateAction('personalCapitalInstructions')}
                  tabIndex={0}
                  className={classes.panel}
                  startIcon={
                    <img src={PersonalCapitalIcon} alt={'Personal Capitial Logo'} className={classes.roundedImage} />
                  }
                />
                <QPanelButton
                  id='import-apple-card'
                  title='Import from Apple Card'
                  subtitle='Step-by-step instructions'
                  onClick={() => dispatchViewStateAction('appleCardInstructions')}
                  tabIndex={0}
                  className={classes.panel}
                  startIcon={<img src={AppleIcon} alt={'Apple Logo'} className={classes.roundedImage} />}
                />
              </>
            )}
            <QPanelButton
              id='import-csv'
              title='Import from a Comma Separated Value (CSV) file'
              subtitle='Import from any source or create your own file with our template'
              onClick={() => dispatchViewStateAction('otherInstructions')}
              tabIndex={0}
              className={classes.panel}
              startIcon={<img src={FileIcon} alt={'csv file icon'} className={classes.csvImage} />}
            />
          </div>
        </div>
      </div>
    ),
    [
      classes.importFromPageStyles,
      classes.importFromPageRight,
      classes.typeTabs,
      classes.fromHeader,
      classes.panel,
      classes.roundedImage,
      classes.csvImage,
      hideTabBar,
      showHistoricalBalances,
      importTypeTab,
      handleImportTypeChange,
    ],
  );

  const mintInstructionsPage = useMemo(
    () => (
      <div className={classes.instructions}>
        {showMintButtons ? (
          <Tabs aria-label='mint tabs' value={mintTabValue} onChange={handleChange} className={classes.tabs}>
            <Tab label='Chrome Extension Import (JSON File)' value='IMPORT_EXTENSION' />
            <Tab label='Mint File Import (CSV File)' value='IMPORT_ACCOUNTS' />
          </Tabs>
        ) : null}
        <div>
          {mintTabValue === 'IMPORT_ACCOUNTS' ? (
            <Typography className={classes.mintInstructions} variant='titleMedium'>
              Upload the Mint CSV file below.
            </Typography>
          ) : (
            <div className={classes.instructions}>
              <Typography variant='paragraphRegular'>
                Using our Chrome Extension allows us to retrieve more complete data than you would get from Mint&apos;s
                CSV export process. This extension will download all of your data into a single JSON file on your
                computer so it is safe and you can review your data before you import it.
              </Typography>
              <div className={classes.mintBalanceInstructions}>
                <div className={classes.balanceImageWidth}>
                  <img src={MintExtension} className={classes.balanceImageWidth} alt={'mint'} />
                </div>
                <div className={classes.mintInstructionsText}>
                  <div className={classes.listItemMint}>
                    <Typography variant='bodyRegular'>
                      1. In your <span className={classes.boldText}>Chrome browser</span>, first click on this link and
                      follow the instructions to <span className={classes.boldText}>install </span>
                      <Link
                        onClick={() =>
                          window.open(
                            'https://chromewebstore.google.com/detail/export-mint-to-simplifi/plgiopiihcaipphaiglabfedjbddbgpd',
                            '_blank',
                          )
                        }
                      >
                        ”Export Mint to Simplifi” Chrome Extension
                        <OpenInNewIcon className={classes.openLink} />
                      </Link>
                    </Typography>
                  </div>
                  <div className={classes.listItemMint}>
                    <Typography variant='bodyRegular'>
                      2. Login to your <span className={classes.boldText}>Mint account</span> from your{' '}
                      <span className={classes.boldText}>Chrome browser</span> at
                      <Link onClick={() => window.open('https://mint.intuit.com ', '_blank')}>
                        {' '}
                        https://mint.intuit.com
                        <OpenInNewIcon className={classes.openLink} />
                      </Link>
                    </Typography>
                  </div>
                  <div className={classes.listItemMint}>
                    <Typography variant='bodyRegular'>
                      3. Click on the <span className={classes.boldText}>Chrome Extension</span> in the upper right
                      corner of your <span className={classes.boldText}>Chrome browser</span> and then select{' '}
                      <span className={classes.boldText}>START MINT DATA EXPORT. </span>
                    </Typography>
                    <Typography variant='bodyRegular'>
                      <WarningRoundedIcon className={classes.warningIcon} />{' '}
                      <span className={classes.boldText}>DO NOT click away</span> until your file has downloaded.
                    </Typography>
                    <QTip
                      arrow
                      solidTheme
                      placement='top'
                      noTippyTip
                      html={
                        <div>
                          Get more complete data from Mint:
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div>• Import transactions and historical balances in one step</div>
                            <div>• Easier linking to accounts</div>
                            <div>• Cleaner category and tag export</div>
                          </div>
                        </div>
                      }
                    >
                      <div className={classes.noteSection}>
                        <Typography variant='paragraphRegular'>
                          <span className={classes.boldText}>Note:</span> This has no limit on the number of
                          transactions!
                        </Typography>
                        <Info className={classes.infoIcon} />
                      </div>
                    </QTip>
                  </div>
                  <div className={classes.listItemMint}>
                    <Typography variant='bodyRegular'>4. Upload the downloaded JSON file below.</Typography>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {errors.length > 0 && errorBox}
        {dragDropComponent}
      </div>
    ),
    [
      classes.instructions,
      classes.tabs,
      classes.mintInstructions,
      classes.mintBalanceInstructions,
      classes.balanceImageWidth,
      classes.mintInstructionsText,
      classes.listItemMint,
      classes.boldText,
      classes.openLink,
      classes.warningIcon,
      classes.noteSection,
      classes.infoIcon,
      showMintButtons,
      mintTabValue,
      handleChange,
      errors.length,
      errorBox,
      dragDropComponent,
    ],
  );

  const mintExtensionClearAll = useMemo(
    () => (
      <div className={classes.instructions}>
        <Typography variant='bodyMedium'>Import All Using Chrome</Typography>
        <Typography variant='paragraphRegular'>
          Before you complete your import, we recommend you clear out any data in your Simplifi account before doing
          this process. Select <span className={classes.boldText}>Clear And Proceed</span> below to avoid creating
          duplicate accounts using this process. You can skip this step by clicking on{' '}
          <span className={classes.boldText}>Proceed Without Clearing.</span>
        </Typography>
        <div className={classes.clearWarningBanner}>
          <WarningRoundedIcon className={classes.warningIcon} />
          <Typography variant='bodyRegular'>
            <span className={classes.boldText}>NOTE:</span> You will lose any connected accounts you have already
            created
          </Typography>
        </div>
        <div className={classes.clearActionSelection}>
          <Typography variant='bodyMedium'>What will happen when I select Clear And Proceed?</Typography>
          <Typography variant='paragraphRegular' className={classes.clearingText}>
            It will <span className={classes.boldText}>delete all of your Accounts.</span>
            It will also <span className={classes.boldText}>delete any Categories</span> you previously imported under
            Mint Expense or Mint Income. It will then proceed with your data import.
          </Typography>
          <div>
            <QButton
              onClick={() => handleExtensionImport(true)}
              variant='contained'
              startIcon={<ReplayCircleFilledIcon />}
            >
              Clear and Proceed
            </QButton>
          </div>
        </div>
      </div>
    ),
    [
      classes.instructions,
      classes.boldText,
      classes.clearWarningBanner,
      classes.warningIcon,
      classes.clearActionSelection,
      classes.clearingText,
      handleExtensionImport,
    ],
  );

  const mintBalancesInstructionsPage = useMemo(
    () => (
      <div className={classes.instructions}>
        {importAccount && (
          <Typography align='center' variant='bodyRegular'>
            Import for {importAccount.name}
          </Typography>
        )}
        <Typography className={classes.mintInstructions} variant='titleMedium'>
          Upload the Mint CSV file below.
        </Typography>
        {errors.length > 0 && errorBox}
        {dragDropComponent}
      </div>
    ),
    [classes.instructions, classes.mintInstructions, importAccount, errors.length, errorBox, dragDropComponent],
  );

  const mintBalanceAccountPage = useMemo(
    () => (
      <div className={classes.mintBalanceAccountSelect}>
        <div className={classes.mintBalanceAccountPadding}>
          <Typography variant='titleMedium'>Choose an account to import historical balance data.</Typography>
          <Typography variant='titleMedium'>Balance data can only be imported one account at a time.</Typography>
        </div>
        {accountDropdown}
      </div>
    ),
    [classes.mintBalanceAccountSelect, classes.mintBalanceAccountPadding, accountDropdown],
  );

  const personalCapitalInstructionsPage = useMemo(
    () => (
      <div className={classes.instructions}>
        <div className={classes.listItem}>
          <Typography variant='bodyMedium' className={classes.step}>
            Step 1
          </Typography>
          <div className={classes.selectAcctRow}>
            <div className={classes.selectAcctLeft}>
              <Typography variant='paragraphRegular'> Choose which account to import to </Typography>
              {accountDropdown}
            </div>
            <Typography variant='paragraphRegular' className={classes.selectAcctRightTxt}>
              Transactions can only be imported to one account at a time.
            </Typography>
          </div>
        </div>

        <div className={classes.listItem}>
          <Typography variant='bodyMedium' className={classes.step}>
            Step 2
          </Typography>
          <Typography variant='paragraphRegular'>
            Go to the transaction page for Credit Card in Personal Capital.
          </Typography>
        </div>

        <div className={classes.listItem}>
          <Typography variant='bodyMedium' className={classes.step}>
            Step 3
          </Typography>
          <Typography variant='paragraphRegular'>On the top of the transaction list, click the CSV icon.</Typography>
        </div>

        <div className={classes.listItem}>
          <Typography variant='bodyMedium' className={classes.step}>
            Step 4
          </Typography>
          {importMessage}
        </div>
        {dragDropComponent}
      </div>
    ),
    [
      classes.listItem,
      classes.step,
      accountDropdown,
      dragDropComponent,
      classes.instructions,
      classes.selectAcctLeft,
      classes.selectAcctRightTxt,
      classes.selectAcctRow,
    ],
  );

  const appleCardInstructionsPage = useMemo(
    () => (
      <div className={classes.instructions}>
        <div className={classes.listItem}>
          <Typography variant='bodyMedium' className={classes.step}>
            Step 1
          </Typography>
          <div className={classes.selectAcctRow}>
            <div className={classes.selectAcctLeft}>
              <Typography variant='paragraphRegular'> Choose which account to import to </Typography>
              {accountDropdown}
            </div>
            <Typography variant='paragraphRegular' className={classes.selectAcctRightTxt}>
              Transactions can only be imported to one account at a time.
            </Typography>
          </div>
        </div>

        <div className={classes.listItem}>
          <Typography variant='bodyMedium' className={classes.step}>
            Step 2
          </Typography>
          <Typography variant='paragraphRegular'>
            Open your Apple Card app and tap on <span className={classes.boldText}>Card Balance.</span>
          </Typography>
        </div>

        <div className={classes.listItem}>
          <Typography variant='bodyMedium' className={classes.step}>
            Step 3
          </Typography>
          <Typography variant='paragraphRegular'>
            Select the statement that you want to import transactions from.
          </Typography>
        </div>

        <div className={classes.listItem}>
          <Typography variant='bodyMedium' className={classes.step}>
            Step 4
          </Typography>
          <Typography variant='paragraphRegular'>
            Tap on <span className={classes.boldText}>Export Transactions</span> then select{' '}
            <span className={classes.boldText}>Comma Separated Values (CSV).</span>
          </Typography>
        </div>

        <div className={classes.listItem}>
          <Typography variant='bodyMedium' className={classes.step}>
            Step 5
          </Typography>
          <Typography variant='paragraphRegular'>
            Tap the share icon on the top right of your screen and send to your computer.
          </Typography>
        </div>

        <div className={classes.listItem}>
          <Typography variant='bodyMedium' className={classes.step}>
            Step 6
          </Typography>
          {importMessage}
        </div>
        {dragDropComponent}
      </div>
    ),
    [
      classes.listItem,
      classes.step,
      accountDropdown,
      dragDropComponent,
      classes.boldText,
      classes.instructions,
      classes.selectAcctLeft,
      classes.selectAcctRightTxt,
      classes.selectAcctRow,
    ],
  );

  const otherInstructionsPage = useMemo(
    () => (
      <div className={classes.instructions}>
        {errors.length > 0 && errorBox}
        {importTypeTab ? (
          // importing historical balances
          <>
            <div className={classes.listItem}>
              <Typography variant='bodyMedium' className={classes.step}>
                Step 1
              </Typography>
              <div className={classes.selectAcctRow}>
                <div className={classes.selectAcctLeft}>
                  <Typography variant='paragraphRegular'> Choose which account to import to </Typography>
                  {accountDropdown}
                </div>
                <Typography variant='paragraphRegular' className={classes.selectAcctRightTxt}>
                  Balance data can only be imported to one account at a time.
                </Typography>
              </div>
            </div>
            <div className={classes.listItem}>
              <Typography variant='bodyMedium' className={classes.step}>
                Step 2
              </Typography>

              <div>
                <Typography variant='paragraphRegular'>
                  Create a spreadsheet using these exact column headers.
                </Typography>
                <Typography variant='paragraphRegular' component='div'>
                  (Or you can&nbsp;
                  <Link href='/files/balance template.csv' download='balance template.csv' type='text/csv'>
                    download our template
                  </Link>
                  )
                </Typography>

                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      {['Date', 'Amount'].map((label) => (
                        <TableCell align='center' className={classes.header} key={label}>
                          <Typography variant='paragraphRegular'>{label}</Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['09/08/2023', '73.00'],
                      ['10/08/2023', '121.12'],
                      ['11/08/2023', '104.21'],
                    ].map((row) => (
                      <TableRow key={row[1]}>
                        {row.map((cell, index) => (
                          <TableCell
                            align={'left'}
                            component={index ? undefined : 'th'}
                            scope={index ? undefined : 'row'}
                            className={classes.cell}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${index}:${cell}`}
                          >
                            <Typography variant='paragraphRegular'>{cell}</Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className={classes.listItem}>
              <Typography variant='bodyMedium' className={classes.step}>
                Step 3
              </Typography>
              <Typography variant='paragraphRegular' className={classes.paragraph}>
                Enter or paste your data into the spreadsheet. Make sure your spreadsheet uses the exact same column
                headers we&apos;ve provided and your dates and amounts are formatted like the above examples.
              </Typography>
            </div>
            <div className={classes.listItem}>
              <Typography variant='bodyMedium' className={classes.step}>
                Step 4
              </Typography>
              <Typography variant='paragraphRegular'>Save your spreadsheet as a .csv file.</Typography>
            </div>
            <div className={classes.listItem}>
              <Typography variant='bodyMedium' className={classes.step}>
                Step 5
              </Typography>
              {importMessage}
            </div>
          </>
        ) : (
          // importing transactions
          <>
            <Typography variant='paragraphRegular' className={classes.miniHeader}>
              How to import from any Comma Separated Value (CSV) file.
            </Typography>
            <div className={classes.listItem}>
              <Typography variant='bodyMedium' className={classes.step}>
                Step 1
              </Typography>
              <div className={classes.selectAcctRow}>
                <div className={classes.selectAcctLeft}>
                  <Typography variant='paragraphRegular'> Choose which account to import to </Typography>
                  {accountDropdown}
                </div>
                <Typography variant='paragraphRegular' className={classes.selectAcctRightTxt}>
                  Transactions can only be imported to one account at a time.
                </Typography>
              </div>
            </div>
            <div className={classes.listItem}>
              <Typography variant='bodyMedium' className={classes.step}>
                Step 2
              </Typography>

              <div>
                <Typography variant='paragraphRegular'>
                  Create a spreadsheet using these exact column headers.
                </Typography>
                <Typography variant='paragraphRegular' component='div'>
                  (Or you can&nbsp;
                  <Link href='files/import template.csv' download='import template.csv' type='text/csv'>
                    download our template
                  </Link>
                  )
                </Typography>

                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      {['Date', 'Payee', 'Amount', 'Tags'].map((label) => (
                        <TableCell align='center' className={classes.header} key={label}>
                          <Typography variant='paragraphRegular'>{label}</Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['3/13/2020', 'Starbucks', '-7', 'Vacation'],
                      ['3/15/2020', 'Uber', '-21.12', 'Hawaii, Vacation'],
                      ['3/17/2020', 'Acme', '1735.21', ''],
                    ].map((row) => (
                      <TableRow key={row[1]}>
                        {row.map((cell, index) => (
                          <TableCell
                            align={'left'}
                            component={index ? undefined : 'th'}
                            scope={index ? undefined : 'row'}
                            className={classes.cell}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${index}:${cell}`}
                          >
                            <Typography variant='paragraphRegular'>{cell}</Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className={classes.listItem}>
              <Typography variant='bodyMedium' className={classes.step}>
                Step 3
              </Typography>
              <Typography variant='paragraphRegular' className={classes.paragraph}>
                {'Enter or paste your transactions into the spreadsheet. ' +
                  "Make sure your spreadsheet uses the exact same column headers that we've provided " +
                  'and your dates and amounts are formatted like the above examples.'}
                <br />
                <br />
                {'Once imported to Simplifi, you can manually assign categories. ' +
                  '(Only needed for payees that you don’t already have category rules set up for.)'}
              </Typography>
            </div>
            <div className={classes.listItem}>
              <Typography variant='bodyMedium' className={classes.step}>
                Step 4
              </Typography>
              <Typography variant='paragraphRegular'>Save your spreadsheet as a .csv file.</Typography>
            </div>
            <div className={classes.listItem}>
              <Typography variant='bodyMedium' className={classes.step}>
                Step 5
              </Typography>
              {importMessage}
            </div>
          </>
        )}
        {dragDropComponent}
      </div>
    ),
    [
      classes.instructions,
      classes.listItem,
      classes.step,
      classes.selectAcctRow,
      classes.selectAcctLeft,
      classes.selectAcctRightTxt,
      classes.table,
      classes.paragraph,
      classes.miniHeader,
      classes.header,
      classes.cell,
      errors.length,
      errorBox,
      importTypeTab,
      accountDropdown,
      dragDropComponent,
    ],
  );

  let dialogHeaderTitle = providerSelected ? `Import from ${providerSelected}` : 'Import Data';
  switch (viewState.currentView) {
    case ViewStateEnum.MINT_SUCCESS:
    case ViewStateEnum.MINT_MATCH:
    case ViewStateEnum.MINT_INSTRUCTIONS:
      dialogHeaderTitle = 'Import from Mint';
      break;
    case ViewStateEnum.MINT_BALANCE:
      dialogHeaderTitle = 'Import Historical Balance from Mint';
      break;
    case ViewStateEnum.PERSONAL_CAPITAL_INSTRUCTIONS:
      dialogHeaderTitle = 'Import from Personal Capital';
      break;
    case ViewStateEnum.MINT_IMPORT_PROGRESS:
      dialogHeaderTitle = 'Import from Mint';
      break;
    case ViewStateEnum.APPLE_CARD_INSTRUCTIONS:
      dialogHeaderTitle = 'Import from Apple Card';
      break;
    case ViewStateEnum.OTHER_INSTRUCTIONS:
      dialogHeaderTitle = importTypeTab ? 'Import Historical Balance' : 'Import from a CSV file';
      break;
    case ViewStateEnum.FILE_PROCESSING:
    default:
      dialogHeaderTitle = 'Import Data';
      break;
  }
  return (
    <StdDialog
      disableBackdropClick={viewState.currentView === ViewStateEnum.FILE_PROCESSING}
      onBack={() => {
        handleDeleteFile();
        setErrors([]); // show error state on current screen but clear anytime that changes
        dispatchViewStateAction(isGSFImport ? 'mintInstructions' : 'importFrom');
      }}
      onClose={onHandleCloseOverride}
      open={open}
      sharedcomponentid={'FILE_IMPORT_DIALOG'}
      showCloseButton={viewState.currentView !== ViewStateEnum.FILE_PROCESSING}
      showBackButton={
        ![
          ViewStateEnum.IMPORT_FROM,
          ViewStateEnum.BALANCE_SUCCESS,
          ViewStateEnum.FILE_PROCESSING,
          ViewStateEnum.MINT_SUCCESS,
          ViewStateEnum.FILE_SUCCEED,
        ].includes(viewState.currentView) && !(isGSFImport && viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS)
      }
      size='lg'
      title={dialogHeaderTitle}
      {...otherProps}
    >
      <>
        <DialogContent
          id='import-dialog'
          className={cx(
            classes.container,
            viewState.currentView === ViewStateEnum.MINT_SUCCESS ||
              viewState.currentView === ViewStateEnum.MINT_EXTENSION_SUCCESS
              ? 'mintNoPadding'
              : '',
          )}
        >
          <div className={classes.contentWrapper}>
            {viewState.currentView === ViewStateEnum.IMPORT_FROM && (
              <div className={classes.contentPlaceholder}>{importFromPage}</div>
            )}
            <div
              className={classes.selectContent}
              style={{
                visibility: viewState.currentView === ViewStateEnum.IMPORT_FROM ? 'visible' : 'hidden',
                left:
                  viewState.currentView === ViewStateEnum.IMPORT_FROM
                    ? 0
                    : (viewState.previousView === ViewStateEnum.IMPORT_FROM && viewState.previousLeft) || '+200%',
              }}
            >
              {importFromPage}
            </div>

            {viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS && (
              <div className={classes.contentPlaceholder}>{mintInstructionsPage}</div>
            )}
            <div
              className={classes.content}
              style={{
                visibility: viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS ? 'visible' : 'hidden',
                left:
                  viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS
                    ? 0
                    : (viewState.previousView === ViewStateEnum.MINT_INSTRUCTIONS && viewState.previousLeft) || '+200%',
              }}
            >
              {mintInstructionsPage}
            </div>

            {viewState.currentView === ViewStateEnum.MINT_BALANCE && (
              <div className={classes.contentPlaceholder}>{mintBalancesInstructionsPage}</div>
            )}
            <div
              className={classes.content}
              style={{
                visibility: viewState.currentView === ViewStateEnum.MINT_BALANCE ? 'visible' : 'hidden',
                left:
                  viewState.currentView === ViewStateEnum.MINT_BALANCE
                    ? 0
                    : (viewState.previousView === ViewStateEnum.MINT_BALANCE && viewState.previousLeft) || '+200%',
              }}
            >
              {mintBalancesInstructionsPage}
            </div>

            {viewState.currentView === ViewStateEnum.MINT_BALANCE_ACCOUNT && (
              <div className={classes.contentPlaceholder}>{mintBalanceAccountPage}</div>
            )}
            <div
              className={classes.content}
              style={{
                visibility: viewState.currentView === ViewStateEnum.MINT_BALANCE_ACCOUNT ? 'visible' : 'hidden',
                left:
                  viewState.currentView === ViewStateEnum.MINT_BALANCE_ACCOUNT
                    ? 0
                    : (viewState.previousView === ViewStateEnum.MINT_BALANCE_ACCOUNT && viewState.previousLeft) ||
                      '+200%',
              }}
            >
              {mintBalanceAccountPage}
            </div>

            {viewState.currentView === ViewStateEnum.MINT_EXTENSION_CLEAR_ALL && (
              <div className={classes.contentPlaceholder}>{mintExtensionClearAll}</div>
            )}
            <div
              className={classes.content}
              style={{
                visibility: viewState.currentView === ViewStateEnum.MINT_EXTENSION_CLEAR_ALL ? 'visible' : 'hidden',
                left:
                  viewState.currentView === ViewStateEnum.MINT_EXTENSION_CLEAR_ALL
                    ? 0
                    : (viewState.previousView === ViewStateEnum.MINT_EXTENSION_CLEAR_ALL && viewState.previousLeft) ||
                      '+200%',
              }}
            >
              {mintExtensionClearAll}
            </div>

            {viewState.currentView === ViewStateEnum.MINT_MATCH && (
              <MintMatchDialog
                handleMintImportMore={handleMintImportMore}
                dispatchViewStateAction={dispatchViewStateAction}
                onFinishLater={onFinishLater}
                onHandleCloseOverride={onHandleCloseOverride}
                accounts={mintAccounts}
                setMintSuccessData={setSuccessData}
              />
            )}
            {viewState.currentView === ViewStateEnum.MINT_SUCCESS && (
              <MintMatchDialog
                handleMintImportMore={handleMintImportMore}
                dispatchViewStateAction={dispatchViewStateAction}
                onFinishLater={onFinishLater}
                onHandleCloseOverride={onHandleCloseOverride}
                successPage
                mintSuccessData={successData}
                isGSFImport={isGSFImport}
                setIsLinking={setIsLinking}
              />
            )}

            {viewState.currentView === ViewStateEnum.PERSONAL_CAPITAL_INSTRUCTIONS && (
              <div className={classes.contentPlaceholder}>{personalCapitalInstructionsPage}</div>
            )}
            <div
              className={classes.content}
              style={{
                visibility:
                  viewState.currentView === ViewStateEnum.PERSONAL_CAPITAL_INSTRUCTIONS ? 'visible' : 'hidden',
                left:
                  viewState.currentView === ViewStateEnum.PERSONAL_CAPITAL_INSTRUCTIONS
                    ? 0
                    : (viewState.previousView === ViewStateEnum.PERSONAL_CAPITAL_INSTRUCTIONS &&
                        viewState.previousLeft) ||
                      '+200%',
              }}
            >
              {personalCapitalInstructionsPage}
            </div>

            {viewState.currentView === ViewStateEnum.MINT_IMPORT_PROGRESS && <ImportFromMint />}
            {viewState.currentView === ViewStateEnum.MINT_EXTENSION_SUCCESS && (
              <MintMatchDialog
                handleMintImportMore={handleMintImportMore}
                dispatchViewStateAction={dispatchViewStateAction}
                onFinishLater={onFinishLater}
                onHandleCloseOverride={onHandleCloseOverride}
                extensionSuccessPage
                mintSuccessData={successData}
                isGSFImport={isGSFImport}
                setIsLinking={setIsLinking}
              />
            )}

            {viewState.currentView === ViewStateEnum.APPLE_CARD_INSTRUCTIONS && (
              <div className={classes.contentPlaceholder}>{appleCardInstructionsPage}</div>
            )}
            <div
              className={classes.content}
              style={{
                visibility: viewState.currentView === ViewStateEnum.APPLE_CARD_INSTRUCTIONS ? 'visible' : 'hidden',
                left:
                  viewState.currentView === ViewStateEnum.APPLE_CARD_INSTRUCTIONS
                    ? 0
                    : (viewState.previousView === ViewStateEnum.APPLE_CARD_INSTRUCTIONS && viewState.previousLeft) ||
                      '+200%',
              }}
            >
              {appleCardInstructionsPage}
            </div>

            {viewState.currentView === ViewStateEnum.OTHER_INSTRUCTIONS && (
              <div className={classes.contentPlaceholder}>{otherInstructionsPage}</div>
            )}
            <div
              className={classes.content}
              style={{
                visibility: viewState.currentView === ViewStateEnum.OTHER_INSTRUCTIONS ? 'visible' : 'hidden',
                left:
                  viewState.currentView === ViewStateEnum.OTHER_INSTRUCTIONS
                    ? 0
                    : (viewState.previousView === ViewStateEnum.OTHER_INSTRUCTIONS && viewState.previousLeft) ||
                      '+200%',
              }}
            >
              {otherInstructionsPage}
            </div>

            {viewState.currentView === ViewStateEnum.FILE_PROCESSING && (
              <div className={classes.textAlignCenter}>
                <LoadingView title={loadingViewTitle} size={128} className={classes.marginForty} />
                <div className={classes.leaveOpenSection}>
                  <Typography variant='bodyMedium'>Please keep this window open while we import your data.</Typography>
                  <Typography variant='bodyRegular'>This may take a few minutes.</Typography>
                </div>
              </div>
            )}

            {viewState.currentView === ViewStateEnum.FILE_SUCCEED && (
              <Fade in mountOnEnter unmountOnExit>
                <div className={classes.statusContainer}>
                  <img alt='thumbs-up' src={checkLargeMotion} style={{ width: 168 }} />
                  <Typography variant='titleMedium'>Success!</Typography>
                  {importAccount ? (
                    <Typography variant='titleMedium'>Transactions are imported into {importAccount.name}</Typography>
                  ) : null}
                </div>
              </Fade>
            )}

            {viewState.currentView === ViewStateEnum.BALANCE_SUCCESS && (
              <Fade in mountOnEnter unmountOnExit>
                <div className={classes.successRoot}>
                  <div className={classes.successStatusContainer}>
                    <img alt='thumbs-up' src={checkLargeMotion} className={classes.thumbsUpWidth} />
                    <div>
                      <Typography variant='titleMedium'>Balance history imported</Typography>
                      {successData?.start && successData?.end && (
                        <Typography variant='paragraphRegular'>
                          Your imported history starts from {DateTime.fromISO(successData.start).toLocaleString()} -{' '}
                          {DateTime.fromISO(successData.end).toLocaleString()}
                        </Typography>
                      )}
                    </div>
                  </div>
                  <div className={classes.finishLater}>
                    <Typography variant='titleMedium'>
                      We found {successData ? successData.balanceHistoryCount : ''} historical dates and balances,
                      imported for {importAccount?.name || 'your account'}.
                    </Typography>
                  </div>
                </div>
              </Fade>
            )}

            {viewState.currentView === ViewStateEnum.FILE_PARTIALLY_SUCCEED && (
              <Fade in mountOnEnter unmountOnExit>
                <div className={classes.statusContainer}>
                  <CancelOutlinedIcon color='error' className={classes.statusImage} />
                  <Typography variant='titleMedium'>Uh oh! The file you submitted has errors.</Typography>
                  <Typography variant='bodyMedium'>
                    {
                      'You can fix the errors and re-upload or, if the errors don’t matter to you, you can continue as is.'
                    }
                  </Typography>
                  <Box maxHeight={200} border={1} overflow='scroll' borderRadius='4px' width='100%' p={2} mt={3} mb={3}>
                    {errors.map((error) => error)}
                  </Box>
                  <QButton
                    variant={QButtonVariantEnum.CONTAINED}
                    onClick={() => {
                      handleDeleteFile();
                      dispatchViewStateAction('tryAgain');
                    }}
                  >
                    Fix & Upload Again
                  </QButton>
                  <Box height={14} />
                  <QButton onClick={onHandleCloseOverride}>Continue As Is</QButton>
                </div>
              </Fade>
            )}

            {viewState.currentView === ViewStateEnum.FILE_FAILED && (
              <Fade in mountOnEnter unmountOnExit>
                <div className={classes.statusContainer}>
                  <CancelOutlinedIcon color='error' className={classes.statusImage} />
                  <Typography variant='titleMedium'>Unable to import</Typography>
                  <Typography variant='bodyMedium'>
                    {filesFailed
                      .keySeq()
                      .filter((clientId: string) => files.get(clientId))
                      .map((clientId: string) => files.get(clientId)?.name)
                      .join(', ')}
                  </Typography>
                  <br />
                  <QButton
                    variant={QButtonVariantEnum.CONTAINED}
                    onClick={() => {
                      handleDeleteFile();
                      dispatchViewStateAction('tryAgain');
                    }}
                  >
                    TRY AGAIN
                  </QButton>
                </div>
              </Fade>
            )}
          </div>
        </DialogContent>

        {viewState.currentView === ViewStateEnum.FILE_SUCCEED && (
          <DialogActions>
            <QButton variant='contained' onClick={onHandleCloseOverride} className={classes.bottomButton}>
              Done
            </QButton>
          </DialogActions>
        )}

        {(viewState.currentView === ViewStateEnum.MINT_BALANCE ||
          viewState.currentView === ViewStateEnum.MINT_BALANCE_ACCOUNT) && (
          <DialogActions>
            <QButton
              variant='contained'
              onClick={
                viewState.currentView === ViewStateEnum.MINT_BALANCE_ACCOUNT
                  ? () => dispatchViewStateAction('mintBalance')
                  : handleImport
              }
              className={classes.bottomButton}
              disabled={
                viewState.currentView === ViewStateEnum.MINT_BALANCE_ACCOUNT
                  ? !importAccount
                  : !(myAcceptedFiles?.length && dispatch)
              }
            >
              Continue
            </QButton>
          </DialogActions>
        )}

        {viewState.currentView === ViewStateEnum.BALANCE_SUCCESS && (
          <DialogActions>
            <QButton
              variant='default'
              onClick={() => {
                handleDeleteFile();
                dispatchViewStateAction(
                  providerSelected === 'Mint-Balances' ? 'mintBalanceAccount' : 'otherInstructions',
                );
              }}
              disableOutline
            >
              Import Another Historical Balance
            </QButton>
            <QButton variant='contained' onClick={onHandleCloseOverride} className={classes.bottomButton}>
              Done
            </QButton>
          </DialogActions>
        )}

        {viewState.currentView === ViewStateEnum.MINT_EXTENSION_CLEAR_ALL && (
          <DialogActions>
            <QButton
              variant='default'
              onClick={() => window.open(getCommonConfig()?.external?.mint_article, '_blank')}
              startIcon={<Help />}
              className={classes.marginRightAuto}
              disableOutline
            >
              Get Help Importing from Mint
            </QButton>
            <QButton onClick={() => handleExtensionImport(false)}>Proceed Without Clearing</QButton>
          </DialogActions>
        )}

        {viewState.currentView === ViewStateEnum.MINT_INSTRUCTIONS && (
          <DialogActions>
            <QButton
              variant='default'
              onClick={() => window.open(getCommonConfig()?.external?.mint_article, '_blank')}
              startIcon={<Help />}
              className={classes.marginRightAuto}
              disableOutline
            >
              Get Help Importing from Mint
            </QButton>
            {mintTabValue === 'IMPORT_EXTENSION' ? (
              <QButton
                variant={QButtonVariantEnum.CONTAINED}
                onClick={() =>
                  acceptableAccounts?.size > 0
                    ? dispatchViewStateAction('mintExtensionClearAll')
                    : handleExtensionImport(false)
                }
                className={classes.bottomButton}
                disabled={!(myAcceptedFiles?.length && dispatch)}
              >
                Continue
              </QButton>
            ) : (
              <QButton
                variant={QButtonVariantEnum.CONTAINED}
                onClick={handleImport}
                className={classes.bottomButton}
                disabled={!(myAcceptedFiles?.length && dispatch)}
              >
                Continue
              </QButton>
            )}
          </DialogActions>
        )}

        {(viewState.currentView === ViewStateEnum.PERSONAL_CAPITAL_INSTRUCTIONS ||
          viewState.currentView === ViewStateEnum.APPLE_CARD_INSTRUCTIONS ||
          viewState.currentView === ViewStateEnum.OTHER_INSTRUCTIONS) && (
          <DialogActions>
            <QButton variant='outlined' onClick={onHandleCloseOverride} className={classes.bottomButton}>
              Cancel
            </QButton>
            <QButton
              variant={QButtonVariantEnum.CONTAINED}
              onClick={handleImport}
              className={classes.bottomButton}
              disabled={!(myAcceptedFiles?.length && dispatch) || !importAccount}
            >
              Import
            </QButton>
          </DialogActions>
        )}

        <input {...getInputProps()} />
      </>
    </StdDialog>
  );
};

export default FileImportDialog;
